/**
 * Paged modes (pure — unit tested): spreads, two-up eligibility, the spread layout, positions, tap
 * zones and swipes.
 *
 * A spread is what shows at once: `per` = 2 pages in landscape two-up ("Tvær síður": pages 1–2,
 * 3–4, …, 0-based [0, 1], [2, 3], …; an odd last page stands alone), or `per` = 1 page in "Heil síða"
 * (one whole page at a time: spread i = page i). `per` defaults to 2; the viewer reads it from the
 * current layout (Layout.perSpread) for every call, so turning, jumps, positions, decoding and
 * overlays share one code path in both modes.
 *
 * The spread layout places EVERY page inside the viewport as if its own spread were showing — the
 * pages of other spreads overlap the current one and are hidden — so the layout never depends on
 * the current spread and turning only flips visibility (instant, no re-layout, no scrolling). At
 * zoom z every coordinate is × z exactly like the scroll layout (content = viewport × z), so pinch
 * zoom, its focal-point math and native panning of a zoomed spread are shared with scroll mode.
 */
import type { PageSize } from '../../shared/dto/library';
import { clamp01, PAD_X, PAD_Y, type Layout, type PageBox, type PagesPerSpread, type VisibleInfo } from './layout';
import type { PagePoint, ViewerPosition } from './types';

/** Two-up is offered when the viewer is landscape and at least this wide (CSS px). */
export const TWO_UP_MIN_WIDTH = 1000;
/** Gap between the two pages of a spread (CSS px at zoom 1). */
export const SPREAD_GAP = 8;
/** A swipe: at least this far horizontally, mostly horizontal, and quick. */
export const SWIPE_MIN_PX = 50;
export const SWIPE_MAX_MS = 800;
export const SWIPE_RATIO = 1.5;

export function twoUpEligible(w: number, h: number): boolean {
  return Number.isFinite(w) && Number.isFinite(h) && w >= TWO_UP_MIN_WIDTH && w > h;
}

/** Pages per spread of a layout (2 when absent). */
export function perSpreadOf(layout: Layout): PagesPerSpread {
  return layout.perSpread === 1 ? 1 : 2;
}

export function spreadCount(pageCount: number, per: PagesPerSpread = 2): number {
  return pageCount > 0 ? Math.ceil(pageCount / per) : 0;
}

/** Spread index containing `page` (clamped to the existing spreads; 0 when there are none). */
export function spreadOf(page: number, pageCount: number, per: PagesPerSpread = 2): number {
  const n = spreadCount(pageCount, per);
  if (n === 0 || !Number.isFinite(page)) return 0;
  return Math.min(n - 1, Math.max(0, Math.floor(page / per)));
}

/**
 * Pages of spread `index`: [a, a + 1], or [a] for an odd last page (two-up); [index] with one page
 * per spread; [] when out of range.
 */
export function spreadPages(index: number, pageCount: number, per: PagesPerSpread = 2): number[] {
  if (!Number.isInteger(index) || index < 0) return [];
  const a = index * per;
  if (a >= pageCount) return [];
  return per === 2 && a + 1 < pageCount ? [a, a + 1] : [a];
}

export function computeSpreads(pageCount: number, per: PagesPerSpread = 2): number[][] {
  return Array.from({ length: spreadCount(pageCount, per) }, (_, i) => spreadPages(i, pageCount, per));
}

/** Pages of the next spread, then the previous one (decode neighbours / overlays: ±1 page with per 1). */
export function spreadNeighbours(index: number, pageCount: number, per: PagesPerSpread = 2): number[] {
  return [...spreadPages(index + 1, pageCount, per), ...spreadPages(index - 1, pageCount, per)];
}

/** `focus` when it is a page of the spread, else the spread's first page. */
function focusIn(pages: readonly number[], focus: number | null | undefined): number {
  return focus !== null && focus !== undefined && pages.includes(focus) ? focus : (pages[0] ?? 0);
}

/**
 * The viewer's visible-page info while spread `index` is showing: current = the page in focus
 * (the page the user went to with goToPage / a jump, when it belongs to the spread), else the
 * spread's first page.
 */
export function spreadVisible(index: number, pageCount: number, focus?: number | null, per: PagesPerSpread = 2): VisibleInfo {
  const pages = spreadPages(index, pageCount, per);
  if (pages.length === 0) return { first: 0, last: -1, current: 0, ordered: [] };
  return { first: pages[0]!, last: pages[pages.length - 1]!, current: focusIn(pages, focus), ordered: pages };
}

function aspect(p: PageSize | undefined): number {
  return p && p.w > 0 && p.h > 0 ? p.h / p.w : Math.SQRT2;
}

export interface SpreadLayoutInput {
  pages: readonly PageSize[];
  viewportW: number;
  viewportH: number;
  zoom: number;
  /** Pages per spread (default 2, two-up). */
  perSpread?: PagesPerSpread;
}

/**
 * Both pages of a spread share one height, as large as fits the viewport height and width (minus
 * padding and the gap); the spread is centred. An odd last page — and every page with one page per
 * spread — is fitted whole and centred alone.
 */
export function computeSpreadLayout(input: SpreadLayoutInput): Layout {
  const { pages } = input;
  const per: PagesPerSpread = input.perSpread === 1 ? 1 : 2;
  const vw = Math.max(1, input.viewportW);
  const vh = Math.max(1, input.viewportH);
  const z = input.zoom;
  const availW = Math.max(40, vw - 2 * PAD_X);
  const availH = Math.max(40, vh - 2 * PAD_Y);
  const boxes: PageBox[] = new Array<PageBox>(pages.length);
  const n = spreadCount(pages.length, per);
  for (let s = 0; s < n; s++) {
    const idx = spreadPages(s, pages.length, per);
    const aspects = idx.map((i) => aspect(pages[i]));
    const gap = idx.length > 1 ? SPREAD_GAP : 0;
    const inv = aspects.reduce((sum, a) => sum + 1 / a, 0);
    const h = Math.max(1, Math.min(availH, (availW - gap) / inv));
    const widths = aspects.map((a) => h / a);
    const total = widths.reduce((sum, w) => sum + w, 0) + gap;
    let x = (vw - total) / 2;
    const y = (vh - h) / 2;
    idx.forEach((page, k) => {
      const w = widths[k]!;
      boxes[page] = { top: y * z, left: x * z, width: w * z, height: h * z };
      x += w + gap;
    });
  }
  return { kind: 'spread', perSpread: per, boxes, width: vw * z, height: vh * z, zoom: z, fit: 'page', padTop: PAD_Y * z, viewportW: vw, viewportH: vh };
}

/**
 * Position while spread `index` shows: page = the page in focus (see spreadVisible; the spread's
 * first page by default), top/left = scroll offsets as fractions of the (zoomed) content size,
 * layout 'spread'.
 */
export function spreadPosition(layout: Layout, index: number, scrollTop: number, scrollLeft: number, focus?: number | null): ViewerPosition {
  return {
    page: focusIn(spreadPages(index, layout.boxes.length, perSpreadOf(layout)), focus),
    top: layout.height > 0 ? Math.max(0, scrollTop) / layout.height : 0,
    left: layout.width > 0 ? Math.max(0, scrollLeft) / layout.width : 0,
    zoom: layout.zoom,
    layout: 'spread',
  };
}

/**
 * Where a position lands in the spread layout: the spread containing its page; a spread position
 * keeps its scroll offsets, a scroll-mode position starts at its page's top-left (0, 0 at zoom 1).
 */
export function spreadFromPosition(layout: Layout, pos: ViewerPosition): { spread: number; top: number; left: number } {
  const spread = spreadOf(pos.page, layout.boxes.length, perSpreadOf(layout));
  if (pos.layout !== 'spread') {
    const page = Number.isFinite(pos.page) ? Math.max(0, Math.min(layout.boxes.length - 1, Math.floor(pos.page))) : 0;
    return { spread, ...spreadScrollForPage(layout, page) };
  }
  const top = Number.isFinite(pos.top) ? Math.max(0, pos.top) * layout.height : 0;
  const left = Number.isFinite(pos.left) ? Math.max(0, pos.left) * layout.width : 0;
  return { spread, top, left };
}

/** Scroll offsets that centre a page point when zoomed (0, 0 at zoom 1 where nothing scrolls). */
export function spreadScrollForPoint(layout: Layout, page: number, x: number, y: number): { top: number; left: number } {
  const box = layout.boxes[Math.max(0, Math.min(layout.boxes.length - 1, page))];
  if (!box) return { top: 0, left: 0 };
  const px = box.left + clamp01(x) * box.width;
  const py = box.top + clamp01(y) * box.height;
  const maxTop = Math.max(0, layout.height - layout.viewportH);
  const maxLeft = Math.max(0, layout.width - layout.viewportW);
  return {
    top: Math.min(maxTop, Math.max(0, py - layout.viewportH / 2)),
    left: Math.min(maxLeft, Math.max(0, px - layout.viewportW / 2)),
  };
}

/** Scroll offsets that show a page's top-left corner (with padding) when zoomed. */
export function spreadScrollForPage(layout: Layout, page: number): { top: number; left: number } {
  const box = layout.boxes[Math.max(0, Math.min(layout.boxes.length - 1, page))];
  if (!box) return { top: 0, left: 0 };
  const maxTop = Math.max(0, layout.height - layout.viewportH);
  const maxLeft = Math.max(0, layout.width - layout.viewportW);
  return {
    top: Math.min(maxTop, Math.max(0, box.top - PAD_Y * layout.zoom)),
    left: Math.min(maxLeft, Math.max(0, box.left - PAD_X * layout.zoom)),
  };
}

/** Normalised point under content coordinates (cx, cy), looking only at the showing pages. */
export function pointInPages(layout: Layout, pages: readonly number[], cx: number, cy: number): PagePoint | null {
  for (const page of pages) {
    const box = layout.boxes[page];
    if (!box || box.width <= 0 || box.height <= 0) continue;
    if (cx < box.left || cx > box.left + box.width || cy < box.top || cy > box.top + box.height) continue;
    return { page, x: (cx - box.left) / box.width, y: (cy - box.top) / box.height };
  }
  return null;
}

/** Tap zones of the viewer: left third → previous (−1), right third → next (1), middle → 0. */
export function tapZone(x: number, width: number): -1 | 0 | 1 {
  if (!(width > 0) || !Number.isFinite(x)) return 0;
  const f = x / width;
  if (f < 1 / 3) return -1;
  if (f > 2 / 3) return 1;
  return 0;
}

/** A finished horizontal swipe: right-to-left → next (1), left-to-right → previous (−1), else 0. */
export function swipeDirection(dx: number, dy: number, ms: number): -1 | 0 | 1 {
  if (!Number.isFinite(dx) || !Number.isFinite(dy) || ms > SWIPE_MAX_MS) return 0;
  if (Math.abs(dx) < SWIPE_MIN_PX || Math.abs(dx) < SWIPE_RATIO * Math.abs(dy)) return 0;
  return dx < 0 ? 1 : -1;
}
